import React, { useEffect, useState, useCallback, useRef } from 'react';
import { get, post } from '../api';
import EquipmentDetailModal from './EquipmentDetailModal';
import './EquipmentPage.css';

// Equipment page — pulls equipment lists from the colleague's Terra 3DInspect
// survey API via our backend proxy. The backend caches per-category for 24h
// and a server-side scheduler refreshes any stale category hourly, so the
// list is up-to-date even without anyone visiting this page.
//
// First click on a category fetches list + metadata from our server; subsequent
// clicks within the same session are instant (client-side cache).

// Required metadata per category. Categories not listed fall back to just
// the shared Type of Operation requirement (matches the modal's default schema).
const REQUIRED_METADATA_FIELDS_BY_CATEGORY = {
  'VT Equipment': [
    'typeOfOperation', 'sensorSize', 'photoResolution', 'videoResolution',
    'videoFps', 'iso', 'photoFormat', 'videoFormat',
  ],
  // UT Equipment requires Type of Operation and the user to have addressed
  // the Accepts-module switch (a boolean → both true and false count).
  // UT Device name/expiration are display-only and don't enter the
  // completeness check.
  'UT Equipment': ['typeOfOperation', 'acceptsModule'],
};
const DEFAULT_REQUIRED_FIELDS = ['typeOfOperation'];

function requiredFieldsFor(category) {
  return REQUIRED_METADATA_FIELDS_BY_CATEGORY[category] || DEFAULT_REQUIRED_FIELDS;
}

function formatDateBadge(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function expiryClass(iso) {
  if (!iso) return 'ddl-expiry-none';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'ddl-expiry-none';
  const now = new Date();
  const diff = (d - now) / (1000 * 60 * 60 * 24);
  if (diff < 0)  return 'ddl-expiry-expired';
  if (diff <= 30) return 'ddl-expiry-soon';
  return 'ddl-expiry-set';
}

function expiryIcon(iso) {
  const cls = expiryClass(iso);
  if (cls === 'ddl-expiry-expired') return '⚠ ';
  if (cls === 'ddl-expiry-soon')    return '⏰ ';
  if (cls === 'ddl-expiry-set')     return '📅 ';
  return '';
}

function isComplete(category, meta) {
  const required = requiredFieldsFor(category);
  if (!meta) return false;
  return required.every((k) => {
    const v = meta[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
}

export default function EquipmentPage() {
  const [categories, setCategories] = useState([]);
  const [active, setActive]         = useState(null);
  const [items, setItems]           = useState([]);
  const [updatedAt, setUpdatedAt]   = useState(null);
  const [metaMap, setMetaMap]       = useState({});
  const [loading, setLoading]       = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState('');
  const [selected, setSelected]     = useState(null);

  // Per-session cache so re-clicking a tab is instant.
  // { [category]: { items, updatedAt, metaMap } }
  const cacheRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await get('/equipment/categories');
        if (cancelled) return;
        setCategories(cats);
        if (cats.length > 0) setActive(cats[0]);
      } catch (e) {
        if (cancelled) return;
        setError(`Failed to load categories: ${e.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function applySnapshot(snap) {
    setItems(snap.items);
    setUpdatedAt(snap.updatedAt);
    setMetaMap(snap.metaMap);
  }

  const loadCategory = useCallback(async (category, { force = false } = {}) => {
    if (!category) return;
    if (!force && cacheRef.current[category]) {
      applySnapshot(cacheRef.current[category]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Two parallel calls: list + bulk metadata.
      const [listResp, metaResp] = await Promise.all([
        get(`/equipment/by-category/${encodeURIComponent(category)}`),
        get(`/equipment/by-category/${encodeURIComponent(category)}/metadata`),
      ]);
      const list = Array.isArray(listResp?.items) ? listResp.items : [];
      list.sort((a, b) => {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        if (!an && !bn) return 0;
        if (!an) return 1;
        if (!bn) return -1;
        return an.localeCompare(bn);
      });
      const map = metaResp?.metadata || {};
      const snap = {
        items: list,
        updatedAt: listResp?.updated_at || null,
        metaMap: map,
      };
      cacheRef.current[category] = snap;
      applySnapshot(snap);
    } catch (e) {
      setItems([]);
      setMetaMap({});
      setError(e.message || 'Failed to load equipment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategory(active);
  }, [active, loadCategory]);

  async function refreshAll() {
    setRefreshing(true);
    setError('');
    try {
      const result = await post('/equipment/refresh-all', {});
      if (result.failed && result.failed.length > 0) {
        const names = result.failed.map((f) => f.category).join(', ');
        setError(`Refresh failed for: ${names}`);
      }
      // Wipe the client cache so all tabs reflect the fresh server data.
      cacheRef.current = {};
      await loadCategory(active, { force: true });
    } catch (e) {
      setError(e.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  }

  function handleMetadataSaved(equipmentId, metadata) {
    setMetaMap((prev) => ({ ...prev, [equipmentId]: metadata }));
    if (cacheRef.current[active]) {
      cacheRef.current[active] = {
        ...cacheRef.current[active],
        metaMap: { ...cacheRef.current[active].metaMap, [equipmentId]: metadata },
      };
    }
  }

  function handleMetadataDeleted(equipmentId) {
    // Drop the orphan row entirely (and its metadata).
    setItems((prev) => prev.filter((it) => !(it.deleted && it.id === equipmentId)));
    setMetaMap((prev) => {
      const next = { ...prev };
      delete next[equipmentId];
      return next;
    });
    if (cacheRef.current[active]) {
      const snap = cacheRef.current[active];
      const nextMap = { ...snap.metaMap };
      delete nextMap[equipmentId];
      cacheRef.current[active] = {
        ...snap,
        items: snap.items.filter((it) => !(it.deleted && it.id === equipmentId)),
        metaMap: nextMap,
      };
    }
  }

  return (
    <div className="equipment-page">
      <div className="equipment-page-header">
        <div className="equipment-title-row">
          <h1 className="equipment-page-title">Equipment</h1>
          {updatedAt && (
            <span className="equipment-last-sync">
              Last synced: {new Date(updatedAt).toLocaleString()}
            </span>
          )}
        </div>
        <button
          className="btn btn-secondary equipment-refresh"
          onClick={refreshAll}
          disabled={refreshing || categories.length === 0}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="equipment-tabs" role="tablist">
        {categories.map((c) => (
          <button
            key={c}
            role="tab"
            aria-selected={c === active}
            className={`equipment-tab${c === active ? ' active' : ''}`}
            onClick={() => setActive(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {error && <div className="equipment-error">{error}</div>}

      <div className="equipment-table-wrap">
        <table className="equipment-table">
          <thead>
            <tr>
              <th className="equipment-col-name">Name</th>
              <th className="equipment-col-manuf">Manufacturer</th>
              <th className="equipment-col-cert">Expiration date</th>
              <th className="equipment-col-op">Type of Operation</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="4" className="equipment-empty">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && !error && (
              <tr><td colSpan="4" className="equipment-empty">No equipment in this category.</td></tr>
            )}
            {!loading && items.map((it) => {
              const display    = formatDateBadge(it.dateEndCertificateValidity);
              const cls        = expiryClass(it.dateEndCertificateValidity);
              const icon       = expiryIcon(it.dateEndCertificateValidity);
              const meta       = metaMap[it.id];
              const complete   = isComplete(active, meta);
              const opLabel    = meta?.typeOfOperation || '—';
              const showIncomplete = !it.deleted && !complete;
              return (
                <tr
                  key={it.id}
                  className={`equipment-row-clickable${it.deleted ? ' equipment-row-deleted' : ''}`}
                  onClick={() => setSelected({ ...it, category: active })}
                >
                  <td>
                    {it.name || '—'}
                    {it.deleted && <span className="row-alert row-alert-deleted">Deleted</span>}
                    {showIncomplete && <span className="row-alert">Incomplete</span>}
                  </td>
                  <td>{it.manufacturer || '—'}</td>
                  <td>
                    {it.deleted ? (
                      <span className="equipment-cert-na">—</span>
                    ) : (
                      <span className={`ddl-expiry-badge ${cls}`}>
                        {display ? <>{icon}{display}</> : <span className="ddl-expiry-placeholder">No date</span>}
                      </span>
                    )}
                  </td>
                  <td className="equipment-op">{opLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <EquipmentDetailModal
          equipment={selected}
          category={selected.category}
          onClose={() => setSelected(null)}
          onSaved={handleMetadataSaved}
          onDeleted={handleMetadataDeleted}
        />
      )}
    </div>
  );
}
